import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, Transition as _Transition, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "relative" }
const _hoisted_2 = { class: "relative" }
const _hoisted_3 = {
  class: "invisible whitespace-nowrap font-medium",
  "aria-hidden": "true"
}
const _hoisted_4 = { class: "absolute inset-0 flex items-center font-medium whitespace-nowrap" }
const _hoisted_5 = { class: "space-y-4" }
const _hoisted_6 = {
  key: 0,
  class: "flex flex-col items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-6 text-center text-red-700"
}
const _hoisted_7 = { class: "text-base font-medium" }
const _hoisted_8 = {
  key: 1,
  class: "hitlist-tabs default-tabs"
}
const _hoisted_9 = { class: "space-x-8 flex border-b border-black border-opacity-10" }
const _hoisted_10 = { class: "flex pr-4" }
const _hoisted_11 = {
  key: 1,
  class: "py-14 text-center px-4 text-gray-500 text-lg"
}
const _hoisted_12 = { class: "flex pr-4" }
const _hoisted_13 = {
  key: 1,
  class: "py-14 text-center px-4 text-gray-500 text-lg"
}

import { computed, ref, watch } from 'vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import { ChevronDown } from 'lucide-vue-next';
import { cloneDeep } from 'lodash-es';
import { Tab, Tabs } from '@makeabledk/vue-ui/components/tabs';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import SwitchButton from '@/components/ui/SwitchButton.vue';
import Button from '@/components/ui/button/Button.vue';
import GenericDashboard from '@/components/common/dashboard/GenericDashboard.vue';
import { useHitlistDashboard } from '@/plugins/store/actions/queries/hitlist/dashboards';
import { buildQuarterKey, buildDynamicQuarterKey, generateQuarterOptions, type DynamicQuarterOption } from '@/util/quarters';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { Dashboard, DashboardItem, DashboardSectionWithItems, TableHeader } from '@/types';
import { i18n } from '@/plugins/internationalization/i18n';

type HitlistScope = 'farms' | 'directory';
type HitlistTab = 'graph' | 'table';


export default /*@__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const quarterOptions = computed<DynamicQuarterOption[]>(() =>
    generateQuarterOptions(new Date(), 4, true, i18n.global.t('hitlist.dropdown.dynamicEstimates') as string)
);
const defaultQuarterKey = computed(() => {
    // First option is always dynamic estimates
    return quarterOptions.value[0]?.key || buildDynamicQuarterKey(new Date().getFullYear(), 1);
});

const selectedScope = ref<HitlistScope>('farms');
const selectedQuarterKey = ref<string>(defaultQuarterKey.value);
const selectedTab = ref<HitlistTab>('graph');
const tbFocusBy = ref<string | null>(null);
const focusInitializedForRender = ref<string | null>(null);

const isWeaners = computed(() => store.getters.getIsWeaners);
const locationIds = computed(() => store.state.selectedFarmIds);

const scope = computed(() => selectedScope.value);
const quarterKey = computed(() => selectedQuarterKey.value);

const render = computed(() => selectedTab.value);

const { data, isFetching, isError, refetch } = useHitlistDashboard({
    scope,
    t: quarterKey,
    isWeaners,
    render,
    locationIds,
    tbFocusBy,
});

const dashboard = ref<Dashboard | undefined>();

function detectDefaultFocusColumn(dash: Dashboard): string | null {
    for (const item of dash.items) {
        if (item.type === 'table' && item.table?.interaction?.mode === 'focus') {
            const header = (item.table.headers as TableHeader[])?.find((h) => h.focusable);
            if (header) return header.value;
        }
    }
    return null;
}

watch(
    [data, isFetching],
    ([newData, fetching]) => {
        dashboard.value = newData ? cloneDeep(newData) : undefined;

        if (!fetching && newData && !tbFocusBy.value && focusInitializedForRender.value !== render.value) {
            const col = detectDefaultFocusColumn(newData);
            if (col) {
                tbFocusBy.value = col;
            }
            focusInitializedForRender.value = render.value;
        }
    },
    { immediate: true }
);

const selectedQuarterLabel = computed(() => quarterOptions.value.find((option) => option.key === selectedQuarterKey.value)?.label || '');

const longestOptionLabel = computed(() =>
    quarterOptions.value.reduce((longest, option) => (option.label.length > longest.length ? option.label : longest), '')
);

const fetchParams = computed(() => ({
    scope: selectedScope.value,
    t: selectedQuarterKey.value,
    isWeaners: isWeaners.value,
    render: selectedTab.value,
    locationIds: locationIds.value,
    tbFocusBy: tbFocusBy.value,
}));

const graphDashboard = computed(() => dashboard.value);
const tableDashboard = computed(() => dashboard.value);

function onUpdateItem({ item }: { item: DashboardItem }) {
    const itemIndex = dashboard.value!.items.findIndex((i) => i.item_no === item.item_no);

    if (itemIndex !== -1) {
        dashboard.value!.items[itemIndex] = item;
    }
}

function onUpdateSectionCollapse({ sectionIndex, isCollapsed }: { sectionIndex: number; isCollapsed: boolean }) {
    if (dashboard.value?.sections?.[sectionIndex]) {
        (dashboard.value.sections[sectionIndex] as DashboardSectionWithItems).is_collapsed = isCollapsed;
    }
}

function onFocusColumn({ column }: { itemNo: number; column: string }) {
    if (tbFocusBy.value !== column) {
        tbFocusBy.value = column;
    }
}

function onTabChange(url: string) {
    const tabId = url.replace('#', '');

    if (tabId === 'graph' || tabId === 'table') {
        selectedTab.value = tabId;
    } else {
        selectedTab.value = 'graph';
    }
}

watch(selectedTab, () => {
    tbFocusBy.value = null;
    focusInitializedForRender.value = null;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DashboardContentLayout, {
    class: "space-y-6",
    heading: _ctx.$t('modules.hitlist.title'),
    "has-period-picker": false,
    "has-pig-type-picker": true
  }, {
    "header-top-right": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(Listbox), {
          modelValue: selectedQuarterKey.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedQuarterKey).value = $event))
        }, {
          default: _withCtx(({ open }) => [
            _createVNode(_unref(ListboxButton), { class: "min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("span", _hoisted_3, _toDisplayString(longestOptionLabel.value), 1),
                  _createElementVNode("span", _hoisted_4, _toDisplayString(selectedQuarterLabel.value), 1)
                ]),
                _createVNode(_unref(ChevronDown), {
                  class: _normalizeClass(["flex-shrink-0 h-5 w-5 transition-transform", { 'rotate-180': open }]),
                  "aria-hidden": "true"
                }, null, 8, ["class"])
              ]),
              _: 2
            }, 1024),
            _createVNode(_Transition, {
              "enter-active-class": "transition duration-100 ease-out",
              "enter-from-class": "transform scale-95 opacity-0",
              "enter-to-class": "transform scale-100 opacity-100",
              "leave-active-class": "transition duration-100 ease-out",
              "leave-from-class": "transform scale-100 opacity-100",
              "leave-to-class": "transform scale-95 opacity-0"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(ListboxOptions), { class: "absolute z-20 mt-1 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(quarterOptions.value, (option) => {
                      return (_openBlock(), _createBlock(_unref(ListboxOption), {
                        key: option.key,
                        value: option.key
                      }, {
                        default: _withCtx(({ active, selected }) => [
                          _createElementVNode("li", {
                            class: _normalizeClass(["py-2 px-3 h-full truncate block w-full text-left cursor-pointer", {
                                        'bg-primary-300 bg-opacity-40': selected,
                                        'hover:bg-gray-100': !selected,
                                        'bg-gray-100': active && !selected,
                                    }])
                          }, _toDisplayString(option.label), 3)
                        ]),
                        _: 2
                      }, 1032, ["value"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_5, [
        _createVNode(SwitchButton, {
          modelValue: selectedScope.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedScope).value = $event)),
          "true-text": _ctx.$t('hitlist.scope.farms'),
          "false-text": _ctx.$t('hitlist.scope.enterprise'),
          "true-value": "farms",
          "false-value": "directory"
        }, null, 8, ["modelValue", "true-text", "false-text"]),
        (_unref(isError))
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('hitlist.loadError')), 1),
              _createVNode(Button, {
                variant: "secondary",
                size: "sm",
                onClick: _unref(refetch)
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('hitlist.retry')), 1)
                ]),
                _: 1
              }, 8, ["onClick"])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(_unref(Tabs), {
                teleport: "#hitlist-tabs-target",
                "active-tab-class": "--active",
                onRewrite: onTabChange
              }, {
                default: _withCtx(() => [
                  _createElementVNode("nav", _hoisted_9, [
                    _createElementVNode("div", _hoisted_10, [
                      _createVNode(_unref(Tab), {
                        name: _ctx.$t('hitlist.tabs.graph'),
                        slug: "graph",
                        default: true
                      }, {
                        default: _withCtx(() => [
                          (graphDashboard.value || _unref(isFetching))
                            ? (_openBlock(), _createBlock(GenericDashboard, {
                                key: 0,
                                data: graphDashboard.value,
                                loading: _unref(isFetching),
                                "fetch-params": fetchParams.value,
                                "fetch-action-type": _unref(ActionType).GetHitlistDashboard,
                                "focused-column": tbFocusBy.value,
                                "no-data-text": _ctx.$t('hitlist.noData'),
                                "onUpdate:item": onUpdateItem,
                                "onUpdate:sectionCollapse": onUpdateSectionCollapse,
                                onFocusColumn: onFocusColumn
                              }, null, 8, ["data", "loading", "fetch-params", "fetch-action-type", "focused-column", "no-data-text"]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_ctx.$t('hitlist.noData')), 1))
                        ]),
                        _: 1
                      }, 8, ["name"])
                    ]),
                    _createElementVNode("div", _hoisted_12, [
                      _createVNode(_unref(Tab), {
                        name: _ctx.$t('hitlist.tabs.table'),
                        slug: "table"
                      }, {
                        default: _withCtx(() => [
                          (tableDashboard.value || _unref(isFetching))
                            ? (_openBlock(), _createBlock(GenericDashboard, {
                                key: 0,
                                data: tableDashboard.value,
                                loading: _unref(isFetching),
                                "fetch-params": fetchParams.value,
                                "fetch-action-type": _unref(ActionType).GetHitlistDashboard,
                                "focused-column": tbFocusBy.value,
                                "no-data-text": _ctx.$t('hitlist.noData'),
                                "onUpdate:item": onUpdateItem,
                                "onUpdate:sectionCollapse": onUpdateSectionCollapse,
                                onFocusColumn: onFocusColumn
                              }, null, 8, ["data", "loading", "fetch-params", "fetch-action-type", "focused-column", "no-data-text"]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(_ctx.$t('hitlist.noData')), 1))
                        ]),
                        _: 1
                      }, 8, ["name"])
                    ])
                  ])
                ]),
                _: 1
              }),
              _cache[2] || (_cache[2] = _createElementVNode("div", {
                id: "hitlist-tabs-target",
                class: "mt-6"
              }, null, -1))
            ]))
      ])
    ]),
    _: 1
  }, 8, ["heading"]))
}
}

})